import { useEffect, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { refusalProse } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/refusalProse';
import { WriteDialog } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/WriteDialog';
import {
  isLiveSeat,
  needsLineChoice,
  SEAT_KIND,
  useGetMoveDestinationsQuery,
  useGetOperatorContextQuery,
  type MoveDestination,
  type MoveDestinationGroup,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Button, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { activeSeatsOfKind, knownSeats, rosterLabel, type SeatHolder } from './memberSeats';
import {
  destinationScopedName,
  DestinationPicker,
  LinePicker,
  ConsequenceLine,
} from './SeatDialogParts';

const { seatActions } = appStrings.modules.coachingDashboard;
const strings = seatActions.addToCoachRosterDialog;

export type AddToCoachRosterDoors = Readonly<{
  isAdding: boolean;
  onAdd: (args: {
    rosterId: number;
    userId: number;
    destinationName: string;
    sourceGrantId: number | null;
  }) => Promise<{ ok: boolean; message: string | null }>;
}>;

type AddToCoachRosterDialogViewProps = Readonly<{
  open: boolean;
  onClose: () => void;
  holder: SeatHolder;
  doors: AddToCoachRosterDoors;
}>;

/**
 * The destinations read spans every operator the caller manages, while this door adds only
 * within the operator the dashboard resolves to. Of that operator's lanes, it adds to coach
 * rosters the person does not already sit on.
 */
const coachRostersToAddTo = (
  groups: readonly MoveDestinationGroup[],
  operatorId: number | null,
  heldRosterIds: ReadonlySet<number>,
): MoveDestinationGroup[] =>
  groups
    .filter((group) => operatorId !== null && group.operator.id === operatorId)
    .map((group) => ({
      ...group,
      rosters: group.rosters.filter(
        (roster) => roster.offering.kind === SEAT_KIND.coach && !heldRosterIds.has(roster.id),
      ),
    }))
    .filter((group) => group.rosters.length > 0);

export const AddToCoachRosterDialogView = ({
  open,
  onClose,
  holder,
  doors,
}: AddToCoachRosterDialogViewProps) => {
  const seats = knownSeats(holder.seats);
  const staffSeat = activeSeatsOfKind(seats, SEAT_KIND.staff)[0];
  const heldRosterIds = new Set(
    seats.filter((seat) => isLiveSeat(seat.status)).map((seat) => seat.roster_id),
  );
  const [pickedRosterId, setPickedRosterId] = useState<number | null>(null);
  const [grantId, setGrantId] = useState<number | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setPickedRosterId(null);
    setGrantId(null);
    setRefusal(null);
  }, [open]);

  const destinations = useGetMoveDestinationsQuery(
    open && staffSeat !== undefined ? { seatId: staffSeat.seat_id } : skipToken,
  );
  const context = useGetOperatorContextQuery(open ? undefined : skipToken);
  const operatorId = context.data?.root?.id ?? null;
  // A read that answered with no operator leaves nothing to scope the rosters to, which is
  // not the same as there being no roster.
  const isOperatorUnread = context.isError || (context.isSuccess && operatorId === null);

  const groups = coachRostersToAddTo(destinations.data ?? [], operatorId, heldRosterIds);
  const rosters: MoveDestination[] = groups.flatMap((group) => group.rosters);
  const onlyRoster = rosters.length === 1 && rosters[0].lines.length > 0 ? rosters[0] : null;
  const destination = rosters.find((roster) => roster.id === pickedRosterId) ?? onlyRoster ?? null;
  const needsLine = destination !== null && needsLineChoice(destination);

  const isSubmittable = destination !== null && !doors.isAdding && (!needsLine || grantId !== null);

  const submit = async () => {
    if (destination === null) return;

    setRefusal(null);

    const outcome = await doors.onAdd({
      rosterId: destination.id,
      userId: holder.id,
      destinationName: destinationScopedName(destination),
      sourceGrantId: needsLine ? grantId : null,
    });

    if (outcome.ok) {
      onClose();

      return;
    }

    setRefusal(noticeForError(outcome.message, strings.failed));
  };

  return (
    <WriteDialog
      id="coaching-dashboard-add-coach-roster-dialog"
      open={open}
      onClose={onClose}
      title={strings.title(holder.name)}
      footer={
        <>
          <Button
            id="coaching-dashboard-add-coach-roster-cancel"
            hierarchy="Secondary gray"
            size="md"
            onClick={onClose}
          >
            {seatActions.cancel}
          </Button>
          <Button
            id="coaching-dashboard-add-coach-roster-submit"
            hierarchy="Primary"
            size="md"
            disabled={!isSubmittable}
            onClick={() => void submit()}
          >
            {doors.isAdding ? strings.submitting : strings.submit}
          </Button>
        </>
      }
    >
      {refusal !== null ? (
        <Typography
          id="coaching-dashboard-add-coach-roster-refusal"
          variant="text-sm-regular"
          role="alert"
        >
          {refusal}
        </Typography>
      ) : null}
      <DestinationPicker
        id="coaching-dashboard-add-coach-roster-destination"
        groups={groups}
        selectedRosterId={destination?.id ?? null}
        onChange={(next) => {
          setPickedRosterId(next);
          setGrantId(null);
          setRefusal(null);
        }}
        isLoading={destinations.isFetching || context.isFetching}
        isError={destinations.isError || isOperatorUnread}
        errorMessage={refusalProse(destinations.error ?? context.error)}
        label={strings.destinationLabel}
        emptyMessage={strings.empty}
      />
      <LinePicker
        id="coaching-dashboard-add-coach-roster-line"
        lines={destination?.lines ?? []}
        selectedGrantId={grantId}
        onChange={setGrantId}
      />
      {staffSeat !== undefined ? (
        <ConsequenceLine
          id="coaching-dashboard-add-coach-roster-keeps-seat"
          text={strings.keepsStaffSeat(rosterLabel(staffSeat))}
        />
      ) : null}
    </WriteDialog>
  );
};
